import { useState, useEffect, useCallback, useMemo } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import {
  fetchAttributeGroups,
  addAttributeGroup,
  removeAttributeGroup,
  addAttributeGroupMember,
  removeAttributeGroupMember,
} from "./declrAttributeGroupInteractionService";
import { fetchAttributeDefinitions } from "../../site-admin/declr_attribute_definition/declrAttributeDefinitionInteractionService";

/**
 * 🎯 DECLARATION ATTRIBUTE GROUP CONTROLLER
 * Per-company screen: create named groups (e.g. "Transport") and compose
 * them out of the global Attribute Definition registry. This is the layer
 * between "attributes exist" and "a company has an actual local capture
 * form" (Z_12_Declr_LocalDeclaration reads these groups to render itself).
 */
export default function DeclrAttributeGroupController(companyId, siteKey) {
  const [groupRows, setGroupRows] = useState([]);
  const [allDefinitions, setAllDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [newGroupLabel, setNewGroupLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [pickerGroupId, setPickerGroupId] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const [rows, defs] = await Promise.all([
        fetchAttributeGroups(companyId, { siteKey }),
        fetchAttributeDefinitions(null, { siteKey }),
      ]);
      setGroupRows(rows || []);
      setAllDefinitions(defs || []);
    } finally {
      setLoading(false);
    }
  }, [companyId, siteKey]);

  useEffect(() => {
    load();
  }, [load]);

  // Group the flat (one-row-per-member) response into { group, members[] }
  const groups = useMemo(() => {
    const byId = new Map();
    for (const row of groupRows) {
      const groupId = GoldenSourceHelper.get(row, "AttributeGroupId");
      if (!byId.has(groupId)) {
        byId.set(groupId, {
          AttributeGroupId: groupId,
          GroupLabel: GoldenSourceHelper.get(row, "GroupLabel"),
          members: [],
        });
      }
      const memberId = GoldenSourceHelper.get(row, "AttributeGroupMemberId");
      if (memberId) {
        byId.get(groupId).members.push({
          AttributeGroupMemberId: memberId,
          AttributeDefinitionId: GoldenSourceHelper.get(row, "AttributeDefinitionId"),
          AttributeLabel: GoldenSourceHelper.get(row, "AttributeLabel"),
          ValueDataType: GoldenSourceHelper.get(row, "ValueDataType"),
        });
      }
    }
    return [...byId.values()];
  }, [groupRows]);

  const handleCreateGroup = async () => {
    if (!newGroupLabel.trim()) return;
    setCreating(true);
    setError("");
    try {
      await addAttributeGroup(companyId, newGroupLabel.trim(), groups.length, { siteKey });
      setNewGroupLabel("");
      await load();
    } catch (err) {
      setError(err.message || "Could not create group.");
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveGroup = async (attributeGroupId) => {
    setBusyId(attributeGroupId);
    setError("");
    try {
      await removeAttributeGroup(attributeGroupId, { siteKey });
      await load();
    } catch (err) {
      setError(err.message || "Could not remove group.");
    } finally {
      setBusyId(null);
    }
  };

  const togglePicker = (attributeGroupId) => {
    setPickerGroupId((prev) => (prev === attributeGroupId ? null : attributeGroupId));
  };

  const handleAddMember = async (attributeGroupId, attributeDefinitionId, existingCount) => {
    setBusyId(attributeDefinitionId);
    setError("");
    try {
      await addAttributeGroupMember(attributeGroupId, attributeDefinitionId, existingCount, { siteKey });
      await load();
    } catch (err) {
      setError(err.message || "Could not add attribute to group.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveMember = async (attributeGroupMemberId) => {
    setBusyId(attributeGroupMemberId);
    setError("");
    try {
      await removeAttributeGroupMember(attributeGroupMemberId, { siteKey });
      await load();
    } catch (err) {
      setError(err.message || "Could not remove attribute from group.");
    } finally {
      setBusyId(null);
    }
  };

  return {
    groups,
    allDefinitions,
    loading,
    error,
    newGroupLabel,
    setNewGroupLabel,
    creating,
    busyId,
    pickerGroupId,
    handleCreateGroup,
    handleRemoveGroup,
    togglePicker,
    handleAddMember,
    handleRemoveMember,
  };
}
